// A desktop-only testing aid: the L key shows the session's exchange log in a
// panel over the page, so a run can be watched without opening devtools. The
// glasses have no L key, so this can never fire on the device itself.
import type { ExperienceSession } from '../../../application/experience/ExperienceSession';
import { formatExchangeLog } from '../../../application/experience/ExperienceSession';

const PANEL_ID = 'gaia-exchange-log-overlay';

function isTextInputFocused(doc: Document): boolean {
  const active = doc.activeElement as HTMLElement | null;
  if (!active) return false;
  const tag = active.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || active.isContentEditable;
}

/**
 * Adds a keydown listener toggling a fixed, top-left, 600x600-max, scrollable,
 * semi-opaque panel showing the session's formatted exchange log, live while
 * open. Returns an unmount function that removes both the listener and the
 * panel. Never steals focus, and never touches Arrow/Enter keys.
 */
export function mountExchangeLogOverlay(session: ExperienceSession, doc: Document = document): () => void {
  let panel: HTMLDivElement | null = null;
  let body: HTMLPreElement | null = null;
  let unsubscribe: (() => void) | null = null;

  function render(): void {
    if (body) body.textContent = formatExchangeLog(session.exchangeLog);
  }

  function open(): void {
    if (panel) return;
    panel = doc.createElement('div');
    panel.id = PANEL_ID;
    panel.style.position = 'fixed';
    panel.style.top = '0';
    panel.style.left = '0';
    panel.style.width = '600px';
    panel.style.maxWidth = '600px';
    panel.style.height = '600px';
    panel.style.maxHeight = '600px';
    panel.style.overflow = 'auto';
    panel.style.background = 'rgba(0, 0, 0, 0.85)';
    panel.style.color = '#fff';
    panel.style.zIndex = '2147483647';
    panel.style.padding = '8px';
    panel.style.boxSizing = 'border-box';
    panel.style.pointerEvents = 'auto';

    const header = doc.createElement('div');
    header.textContent = 'Exchange log (L to close)';
    header.style.fontFamily = 'monospace';
    header.style.fontSize = '14px';
    header.style.marginBottom = '4px';
    header.style.opacity = '0.8';
    panel.appendChild(header);

    body = doc.createElement('pre');
    body.style.fontFamily = 'monospace';
    body.style.fontSize = '14px';
    body.style.whiteSpace = 'pre-wrap';
    body.style.margin = '0';
    panel.appendChild(body);

    render();
    doc.body.appendChild(panel);
    unsubscribe = session.subscribe(() => render());
  }

  function close(): void {
    unsubscribe?.();
    unsubscribe = null;
    panel?.remove();
    panel = null;
    body = null;
  }

  function onKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'l' && event.key !== 'L') return;
    if (isTextInputFocused(doc)) return;
    if (panel) close();
    else open();
  }

  doc.addEventListener('keydown', onKeyDown);

  return () => {
    doc.removeEventListener('keydown', onKeyDown);
    close();
  };
}
